import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getCustomFees, createCustomFee, updateCustomFee, deleteCustomFee, getAssociatedExperiences } from "store/custom-fees"
import { ACTION_TYPES } from "../modal/AssociatedExperiencesConfirmModal"

export const useCustomFees = ({ enabled = false } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const customFees = useSelector((state) => state.customFees?.items || [])
  const customFeesLoading = useSelector((state) => state.customFees?.loading || false)
  const isCreating = useSelector((state) => state.customFees?.createCustomFee?.loading || false)
  const isUpdating = useSelector((state) => state.customFees?.updateCustomFee?.loading || false)
  const isDeleting = useSelector((state) => state.customFees?.deleteCustomFee?.loading || false)
  const isCheckingAssociations = useSelector((state) => state.customFees?.associatedExperiences?.loading || false)
  const associatedExperiences = useSelector((state) => state.customFees?.associatedExperiences?.data || [])
  const associatedExperiencesFailed = useSelector((state) => state.customFees?.associatedExperiences?.failed || false)

  const isMutating = isCreating || isUpdating || isDeleting || isCheckingAssociations

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [feeToDelete, setFeeToDelete] = useState(null)
  const [feeToEdit, setFeeToEdit] = useState(null)

  // Associated experiences confirmation modal state
  const [assocExpModalOpen, setAssocExpModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState(null) // { type: ACTION_TYPES, fee: record }

  // Activate confirmation modal state
  const [activateModalOpen, setActivateModalOpen] = useState(false)
  const [feeToActivate, setFeeToActivate] = useState(null)

  useEffect(() => {
    if (enabled) dispatch(getCustomFees())
  }, [dispatch, enabled])

  const handleAddFee = async (values) => {
    try {
      const feeData = {
        title: values.title,
        price: Number.parseFloat(values.price),
        fee_type: values.fee_type
      }
      if (feeToEdit) {
        await dispatch(updateCustomFee(feeToEdit.id, feeData))
      } else {
        await dispatch(createCustomFee(feeData))
      }
      dispatch(getCustomFees())
      setIsModalOpen(false)
      setFeeToEdit(null)
    } catch (error) {
      console.error("Error saving custom fee:", error)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteCustomFee(feeToDelete.id))
      dispatch(getCustomFees())
      setDeleteModalOpen(false)
      setFeeToDelete(null)
    } catch (error) {
      console.error("Error deleting custom fee:", error)
    }
  }

  // Step 1: check associated experiences before deactivate/delete
  const openAssocExpModal = async (fee, actionType) => {
    setPendingAction({ type: actionType, fee })
    setAssocExpModalOpen(true)
    try {
      await dispatch(getAssociatedExperiences(fee.id))
    } catch (error) {
      // failed state is handled in reducer; modal stays open and blocks confirm
      console.error("Error fetching associated experiences:", error)
    }
  }

  // Step 2: user confirmed — run the real action
  const handleAssocExpConfirm = async () => {
    if (!pendingAction) return
    const { type, fee } = pendingAction
    try {
      if (type === ACTION_TYPES.DELETE) {
        await dispatch(deleteCustomFee(fee.id))
      } else {
        await dispatch(updateCustomFee(fee.id, { is_active: !fee.is_active }))
      }
      dispatch(getCustomFees())
      setAssocExpModalOpen(false)
      setPendingAction(null)
    } catch (error) {
      console.error("Error performing action:", error)
    }
  }

  const handleAssocExpCancel = () => {
    setAssocExpModalOpen(false)
    setPendingAction(null)
  }

  const handleActivateConfirm = async () => {
    try {
      await dispatch(updateCustomFee(feeToActivate.id, { is_active: true }))
      dispatch(getCustomFees())
      setActivateModalOpen(false)
      setFeeToActivate(null)
    } catch (error) {
      console.error("Error activating custom fee:", error)
    }
  }

  const handleActivateCancel = () => {
    setActivateModalOpen(false)
    setFeeToActivate(null)
  }

  const getMenuItems = (record) => [
    {
      key: "edit",
      label: t("settings.action_edit"),
      onClick: () => {
        setFeeToEdit(record)
        setIsModalOpen(true)
      }
    },
    {
      key: "toggle",
      label: record.is_active ? t("settings.action_deactivate") : t("settings.action_activate"),
      onClick: () => {
        if (record.is_active) {
          openAssocExpModal(record, ACTION_TYPES.DEACTIVATE)
        } else {
          setFeeToActivate(record)
          setActivateModalOpen(true)
        }
      }
    },
    {
      key: "delete",
      label: t("settings.action_delete"),
      danger: true,
      onClick: () => openAssocExpModal(record, ACTION_TYPES.DELETE)
    }
  ]

  return {
    customFees,
    customFeesLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    deleteModalOpen,
    feeToEdit,
    handleAddFee,
    handleDeleteConfirm,
    handleDeleteCancel: () => {
      setDeleteModalOpen(false)
      setFeeToDelete(null)
    },
    openAddModal: () => {
      setFeeToEdit(null)
      setIsModalOpen(true)
    },
    closeModal: () => {
      setIsModalOpen(false)
      setFeeToEdit(null)
    },
    getMenuItems,
    // associated experiences modal
    assocExpModalOpen,
    pendingAction,
    associatedExperiences,
    isCheckingAssociations,
    associatedExperiencesFailed,
    isConfirming: isDeleting || isUpdating,
    handleAssocExpConfirm,
    handleAssocExpCancel,
    // activate modal
    activateModalOpen,
    handleActivateConfirm,
    handleActivateCancel
  }
}
